import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowRight, GraduationCap, Globe, Award, BookOpen, TrendingUp, ChevronRight } from 'lucide-react';
import BlogCard from '@/components/BlogCard';
import UniversityCard from '@/components/UniversityCard';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [usaUniversities, setUsaUniversities] = useState([]);
  const [europeUniversities, setEuropeUniversities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [blogsRes, usaRes, europeRes] = await Promise.all([
          axios.get(`${API}/blogs?featured=true&limit=3`),
          axios.get(`${API}/universities?country=USA&limit=3`),
          axios.get(`${API}/universities?country=UK&limit=3`)
        ]);
        
        setFeaturedBlogs(blogsRes.data);
        setUsaUniversities(usaRes.data);
        setEuropeUniversities(europeRes.data);
      } catch (error) {
        console.error('Error fetching homepage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const stats = [
    { icon: GraduationCap, number: '500+', label: 'Universities' },
    { icon: Globe, number: '50+', label: 'Countries' },
    { icon: Award, number: '1000+', label: 'Scholarships' },
    { icon: BookOpen, number: '5000+', label: 'Online Courses' },
  ];

  return (
    <div className="pt-[100px]">
      {/* Hero Section - Premium Editorial Style */}
      <section className="relative bg-primary overflow-hidden" data-testid="hero-section">
        {/* Texture overlay */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/2872168/pexels-photo-2872168.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary/90" />
        
        <div className="container-custom relative z-10">
          <div className="py-20 md:py-28 lg:py-32">
            <div className="max-w-4xl">
              {/* Breaking label */}
              <div className="flex items-center gap-2 mb-6 animate-fade-in">
                <span className="bg-secondary text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  Trending
                </span>
                <span className="text-white/70 text-sm">2026 University Rankings Released</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-white mb-6 leading-tight animate-slide-up">
                Your Trusted Source for Higher Education & Career Growth
              </h1>
              <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed max-w-2xl font-light">
                Discover top universities, trending skills, scholarships, and career-building programs across the US and Europe.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/universities">
                  <Button 
                    size="lg" 
                    className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-primary font-bold px-8 rounded-sm shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5" 
                    data-testid="explore-universities-btn"
                  >
                    Explore Universities
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="bg-transparent text-white border-2 border-white/30 hover:bg-white hover:text-primary px-8 rounded-sm transition-all duration-300" 
                    data-testid="browse-courses-btn"
                  >
                    Browse Online Courses
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats Row */}
            <div className="mt-16 pt-10 border-t border-white/10">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center md:text-left">
                    <stat.icon className="w-8 h-8 text-[hsl(var(--gold))] mb-3 mx-auto md:mx-0" />
                    <div className="text-3xl md:text-4xl font-playfair font-bold text-[hsl(var(--gold))] mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm uppercase tracking-widest text-white/60">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Education News */}
      <section className="section-padding bg-white" data-testid="featured-news-section">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-tag text-secondary mb-2 block">Latest Updates</span>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary" data-testid="homepage-deals-heading">
                Deals for Students
              </h2>
            </div>
            <Link to="/blog" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors group" data-testid="view-all-news-btn">
              View All Articles
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredBlogs.map((post) => (
                <BlogCard key={post.post_id} post={post} />
              ))}
            </div>
          )}

          <Link to="/blog" className="md:hidden flex items-center justify-center gap-2 mt-8 text-primary font-semibold hover:text-secondary transition-colors">
            View All Articles
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      {/* Top Universities in USA */}
      <section className="section-padding bg-muted" data-testid="usa-universities-section">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-tag text-secondary mb-2 block">USA Education</span>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary">
                Top Universities in USA
              </h2>
            </div>
            <Link to="/study-usa" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors group" data-testid="view-all-usa-btn">
              View All
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] bg-white animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {usaUniversities.map((uni) => (
                <UniversityCard key={uni.university_id} university={uni} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Top Universities in Europe */}
      <section className="section-padding bg-white" data-testid="europe-universities-section">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="label-tag text-secondary mb-2 block">European Education</span>
              <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary">
                Top Universities in Europe
              </h2>
            </div>
            <Link to="/study-europe" className="hidden md:flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors group" data-testid="view-all-europe-btn">
              View All
              <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse rounded-sm" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {europeUniversities.map((uni) => (
                <UniversityCard key={uni.university_id} university={uni} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Choose Us - Feature Grid */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="label-tag text-secondary mb-2 block">Why InsideHigherEducation</span>
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-primary">
              Your Partner in Academic Success
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Expert Analysis', desc: 'In-depth reviews and rankings from education experts worldwide.' },
              { title: 'Verified Data', desc: 'Accurate, up-to-date information on programs, fees, and requirements.' },
              { title: 'Career Guidance', desc: 'Resources to help you transition from education to career success.' },
            ].map((feature, i) => (
              <div key={i} className="bg-white p-8 border-l-4 border-[hsl(var(--gold))] hover:shadow-lg transition-all duration-300">
                <h3 className="text-xl font-playfair font-bold text-primary mb-3">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/2872168/pexels-photo-2872168.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-playfair font-bold text-white mb-4">
              Ready to Start Your Educational Journey?
            </h2>
            <p className="text-lg text-white/80 mb-10 leading-relaxed">
              Explore thousands of programs, scholarships, and opportunities tailored to your goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/scholarships">
                <Button 
                  size="lg" 
                  className="bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold))]/90 text-primary font-bold px-8 rounded-sm shadow-lg" 
                  data-testid="explore-scholarships-btn"
                >
                  Explore Scholarships
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-transparent text-white border-2 border-white/30 hover:bg-white hover:text-primary px-8 rounded-sm transition-all" 
                  data-testid="contact-us-btn"
                >
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
